/**
 * Camera2D Component
 *
 * Orthographic camera for 2D rendering with post-processing support.
 */

import {
  component,
  displayName,
  icon,
  color,
  category,
  allowMultiple,
  requireComponent,
  field,
  customEditor,
} from "../attributes";
import { EngineComponent } from "../scripting/EngineComponent";

@component("core.Camera2D")
@customEditor("CameraEditor")
@displayName("Camera 2D")
@icon("Video")
@color("#74b9ff")
@category("Rendering")
@allowMultiple(false)
@requireComponent(["core.Transform"])
export class Camera2D extends EngineComponent {
  // Projection
  @field({ type: "i32", hidden: true, tooltip: "0 = orthographic" })
  projection: number = 0;

  @field({ type: "f32", label: "Orthographic Size", min: 0.1, max: 100 })
  orthographicSize: number = 5;

  @field({ type: "f32", label: "Near 2 Clip" })
  nearClip: number = -1000;

  @field({ type: "f32", label: "Far Clip" })
  farClip: number = 1000;

  // Clear
  @field({ type: "i32", label: "Clear Flags", tooltip: "0 = solid color, 1 = nothing" })
  clearFlags: number = 0;

  @field({ type: "color", label: "Background Color" })
  backgroundColor: { r: number; g: number; b: number; a: number } = { r: 0.07, g: 0.07, b: 0.07, a: 1 };

  // Rendering
  @field({ type: "i32", label: "Culling Mask", tooltip: "-1 = Everything" })
  cullingMask: number = -1;

  @field({ type: "i32", label: "Depth" })
  depth: number = 0;

  @field({ type: "object", hidden: true })
  viewport: { x: number; y: number; width: number; height: number } = { x: 0, y: 0, width: 1, height: 1 };

  // Rendering pipeline
  @field({ type: "bool", label: "HDR", tooltip: "Render to an HDR target and tonemap to screen. Colors above 1 stay bright (and glow under bloom) instead of clipping." })
  hdr: boolean = true;

  // Post-Processing
  @field({ type: "bool", label: "Post-Processing" })
  postProcessEnabled: boolean = false;

  @field({ type: "object", hidden: true })
  postProcessStack: unknown[] = [];

  // ── Runtime API (Unity-style; proxies to engine.camera for the bound entity) ──
  // Not @field → not serialized. Distinct method names (not getter/setter on `orthographicSize`)
  // to avoid shadowing the serialized @field. Usage: `this.getComponent(Components.Camera2D)?.makeMain()`.

  /** Live orthographic half-height (reads through engine.camera; falls back to the @field). */
  getOrthoSize(): number {
    return this.engine?.camera?.getOrthographicSize?.(this.entity) ?? this.orthographicSize;
  }

  /** Set the orthographic size at runtime (zoom); the renderer re-reads next frame. */
  setOrthoSize(size: number): void {
    this.engine?.camera?.setOrthographicSize?.(this.entity, size);
  }

  /** Whether this camera is the active/live one. */
  isActive(): boolean {
    return this.engine?.camera?.isActive?.(this.entity) ?? false;
  }

  /** Force this camera to become the active/live one. */
  makeMain(): void {
    this.engine?.camera?.setActive?.(this.entity);
  }
}
